const KEY = "scalpmania.theme";

export type ThemePref = "light" | "dark" | "system";
export type Theme = "light" | "dark";

/** Stored preference, or "system" when the operator hasn't chosen. */
export function getThemePref(): ThemePref {
	try {
		const v = localStorage.getItem(KEY);
		return v === "light" || v === "dark" || v === "system" ? v : "system";
	} catch {
		// localStorage may be blocked (private browsing, enterprise policy, sandboxed iframe).
		// Fall back to system preference.
		return "system";
	}
}

/**
 * "system" follows prefers-color-scheme, falling back to dark when the OS
 * states no preference — this runs on a screen that's often checked at night.
 */
export function resolveTheme(pref: ThemePref): Theme {
	if (pref !== "system") return pref;
	if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
	return "dark";
}

export function applyTheme(pref: ThemePref): void {
	const theme = resolveTheme(pref);
	const root = document.documentElement;
	root.dataset.theme = theme;
	root.style.colorScheme = theme;
}

export function setThemePref(pref: ThemePref): void {
	try {
		localStorage.setItem(KEY, pref);
	} catch {
		// localStorage may be blocked (private browsing, enterprise policy, sandboxed iframe).
		// Still apply the theme for this session even if we can't persist it.
	}
	applyTheme(pref);
}

/** Re-run the callback when the OS flips, so "system" stays live. */
export function subscribeSystemTheme(cb: () => void): () => void {
	const mq = window.matchMedia("(prefers-color-scheme: light)");
	mq.addEventListener("change", cb);
	return () => mq.removeEventListener("change", cb);
}
